import React, { forwardRef, InputHTMLAttributes, useState } from "react";
import styles from "./FormField.module.css";
import { Form, InputGroup, Image, Spinner } from "react-bootstrap";
import cn from "classnames";
import { ASSESTS } from "../../../constant/assest";

export enum IFieldTheme {
  DEFAULT = "fieldDefault",
  LIGHT = "fieldLight",
  LIGHT2 = 'fieldLightTwo',
  WHITE = 'fieldWhite',
  GREEN = "fieldGreen",
}

export enum IFieldType {
  TEXT = "text",
  PASSWORD = "password",
  EMAIL = "email",
  NUMBER = "number",
  MONTH = "month",
  SEARCH = "search",
  TEL = "tel",
  TIME = "time",
  URL = "url",
  DATE = "date",
}

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
>;

interface IFormFieldProps extends NativeInputProps {
  fieldType: IFieldType;
  label?: string;
  placeholder?: string;
  showError?: boolean;
  errorText?: string;
  disabled?: boolean;
  showEyeIcon?: boolean;
  fieldTheme?: IFieldTheme;
  showEditIcon?: boolean;
  editIcon?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  successText?: string;
  isSuccess?: boolean;
  customEndAdornment?: React.ReactNode;

  /** Re-defined to match react-bootstrap */
  value?: string | number | string[];
  defaultValue?: string | number | string[];
}

const FormField = forwardRef<HTMLInputElement, IFormFieldProps>(
  (props, ref) => {
    const {
      fieldTheme = IFieldTheme.DEFAULT,
      fieldType,
      label,
      placeholder,
      showError,
      errorText,
      disabled,
      showEyeIcon,
      showEditIcon,
      editIcon,
      className,
      isLoading,
      successText,
      isSuccess,
      customEndAdornment,
      ...restProps
    } = props;

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword((prev) => !prev);

    return (
      <div className={cn(styles.formField, className, styles[fieldTheme])}>
        {label && <Form.Label className={styles.label}>{label}</Form.Label>}

        <InputGroup className={cn(showEditIcon && 'editInputWrap', styles.inputGroup)}>
          <Form.Control
            ref={ref}
            type={showEyeIcon && showPassword ? 'text' : fieldType}
            placeholder={placeholder}
            disabled={disabled}
            className={styles.formControl}
            {...restProps}
          />

          {showEditIcon && (
            <button type="button" className={'eitBtn'}>
              {editIcon}
            </button>
          )}

          {fieldType === IFieldType.PASSWORD && showEyeIcon && (
            <InputGroup.Text className={styles.adornment}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={togglePassword}
              >
                <Image
                  src={showPassword ? ASSESTS.EYE : ASSESTS.EYESLASH}
                  width={16}
                  height={16}
                  alt={showPassword ? "Eye" : "EyeSlash"}
                />
              </button>
            </InputGroup.Text>
          )}

          {isLoading && (
            <InputGroup.Text className={styles.adornment}>
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
          
          {!isLoading && isSuccess && (
            <InputGroup.Text className={styles.adornment}>
              <Image src={ASSESTS.CHECKEDGREEN} width={16} height={16} alt="Success" />
            </InputGroup.Text>
          )}

          {customEndAdornment && (
            <InputGroup.Text className={styles.adornment}>
              {customEndAdornment}
            </InputGroup.Text>
          )}
        </InputGroup>

        {showError && <div className={styles.errorText}>{errorText}</div>}
        
        {!showError && !isLoading && isSuccess && successText && (
          <div className={styles.successText}>{successText}</div>
        )}
      </div>
    );
  }
);

export default FormField;
